import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

/** A top-level documentation section — one entry in the sidebar. */
export function Section({ id, title, lead, children }) {
  return (
    <section id={id}>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">{title}</h2>
      {lead && (
        <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">{lead}</p>
      )}
      {children}
    </section>
  );
}

/** One screen or topic inside a section — an indented sidebar entry. */
export function Sub({ id, title, children }) {
  return (
    <div id={id} className="mb-8 scroll-mt-28">
      <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

/** The standard bordered panel most documentation content sits in. */
export function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
      {title && (
        <h4 className="font-semibold text-slate-900 dark:text-white">{title}</h4>
      )}
      {children}
    </div>
  );
}

/** A neutral aside — useful detail, nothing at stake. */
export function Note({ children }) {
  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3">
      <p className="text-blue-700 dark:text-blue-400 text-xs leading-relaxed">{children}</p>
    </div>
  );
}

/** Something that can lose work if ignored. */
export function Warn({ children }) {
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
      <p className="text-amber-700 dark:text-amber-400 text-xs leading-relaxed">{children}</p>
    </div>
  );
}

/** Marks a screen or ability that is new in 3.0 and had no version 2 equivalent. */
export function New({ children = 'New in 3.0' }) {
  return (
    <span className="ml-2 align-middle inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-primary-400/15 text-primary-600 dark:text-primary-400">
      {children}
    </span>
  );
}

/** A labelled list of settings or fields. */
export function Fields({ items }) {
  return (
    <ul className="space-y-2">
      {items.map(([term, description]) => (
        <li key={term}>
          <strong className="text-slate-800 dark:text-slate-200">{term}</strong>
          {description ? <> — {description}</> : null}
        </li>
      ))}
    </ul>
  );
}

/** Inline code, for keys, ids and file names. */
export function Code({ children }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-xs">
      {children}
    </code>
  );
}

/**
 * A screenshot of the app, swapped for the reader's theme.
 *
 * Both variants come out of the same capture run, so a light-mode reader sees a
 * light-mode program: a documentation page that shows the dark theme to someone
 * running the light one is describing a screen they cannot find.
 */
export function Shot({ name, alt, caption }) {
  const { theme } = useTheme();
  const [failed, setFailed] = useState(false);
  const variant = theme === 'light' ? 'light' : 'dark';

  // A capture that has not been imported yet must not leave a broken image icon
  // in the middle of a paragraph. The prose stands on its own; the picture is
  // support for it.
  if (failed) return null;

  return (
    <figure className="my-5">
      <img
        src={`/images/docs/${variant}/${name}.png`}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="w-full rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm"
      />
      {caption && (
        <figcaption className="mt-2 text-xs text-slate-500 dark:text-slate-500">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
